import {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { Button, Popover, Position, Spinner } from "@blueprintjs/core"
import RouteStopView from "./RouteStopView"
import { LocationEditView } from "@/components/routeStops/LocationView"
import { DriverContext } from "@/contexts/DriverContext"
import {
  findCurrentTarget,
  findRoute2g,
  findRouteConfig,
  isStopActive,
  isStopLate,
  isStopPending,
  Route2g,
  RouteStop,
  shouldAlert,
} from "@/reducers/routes2gSlice"
import { Locate, Plus } from "lucide-react"
import { Location } from "@/reducers/locationsSlice"
import { captureError } from "@/ErrorHandlers"
import css from "./RouteStopList.module.css"
import { isMobile } from "@/utils/detectDevice"
import { useAppSelector } from "@/reducers/hooks"
import { selectLocation } from "@/reducers/locationsSelectors"
import useDebouncedValue from "@/hooks/useDebouncedValue"
import { useMediaQuery } from "@react-hookz/web"
import { Address, useReverseGeocoder } from "@/hooks/useReverseGeocode"
import { Button as NewButton } from "@/components/ui/button"
import { AvoidType } from "@/api/adHocCommands"
import { getDisplayedStopDistance } from "@/lib/stopDistance"
import { useStopToStopDistance } from "@/hooks/useStopToStopDistance"
import { moveStop as moveRouteStop, newStop } from "@/lib/routeStopEdits"
import { useTranslation } from "react-i18next"
import { STOP_NUMBERS_FLAG, stopNumbers } from "@/lib/stopNumbers"
import { useFeatureFlagEnabled } from "posthog-js/react"

export type RouteStopDiff = {
  added: Array<{ index: number; stop: RouteStop }>
  removed: Array<{ index: number; stop: RouteStop }>
  modified: Array<{ index: number; stop: RouteStop; changedKeys: string[] }>
  hasNonPassthroughChanges: boolean
  hasPassthroughChanges: boolean
  hasAvoidChanges: boolean
  hasLoadTypeChanges: boolean
  hasOrderChanges: boolean
  hasActionOnlyChanges: boolean
}

export const calculateRouteStopDiff = (
  originalStops: RouteStop[],
  updatedStops: RouteStop[],
): RouteStopDiff => {
  const diff: RouteStopDiff = {
    added: [],
    removed: [],
    modified: [],
    hasNonPassthroughChanges: false,
    hasPassthroughChanges: false,
    hasAvoidChanges: false,
    hasLoadTypeChanges: false,
    hasOrderChanges: false,
    hasActionOnlyChanges: false,
  }

  // Create maps for quick lookup (only for stops with IDs)
  const originalMap = new Map(
    originalStops
      .filter((stop) => stop.id)
      .map((stop, idx) => [stop.id, { stop, index: idx }]),
  )
  const updatedMap = new Map(
    updatedStops
      .filter((stop) => stop.id)
      .map((stop, idx) => [stop.id, { stop, index: idx }]),
  )

  // Find added stops (stops without ID OR stops with ID not in original)
  updatedStops.forEach((stop, index) => {
    if (!stop.id || !originalMap.has(stop.id)) {
      diff.added.push({ index, stop })
    }
  })

  // Find removed stops (stops with ID in original but not in updated)
  originalStops.forEach((stop, index) => {
    if (stop.id && !updatedMap.has(stop.id)) {
      diff.removed.push({ index, stop })
    }
  })

  // Find modified stops (in both with matching IDs and location changed)
  updatedStops.forEach((stop, index) => {
    if (stop.id && originalMap.has(stop.id)) {
      const originalStop = originalMap.get(stop.id)!.stop
      const changedKeys: string[] = []

      // Compare each property to find what changed
      const allKeys = new Set([
        ...Object.keys(originalStop),
        ...Object.keys(stop),
      ])

      allKeys.forEach((key) => {
        const originalValue = (originalStop as any)[key]
        const updatedValue = (stop as any)[key]

        // Deep comparison for objects/arrays, shallow for primitives
        if (JSON.stringify(originalValue) !== JSON.stringify(updatedValue)) {
          changedKeys.push(key)
        }
      })

      // Only add to modified if location has changed
      if (changedKeys.includes("location")) {
        diff.modified.push({ index, stop, changedKeys })
      }

      // Check if only action changed (no location change)
      if (
        changedKeys.includes("action") &&
        !changedKeys.includes("location") &&
        changedKeys.every(
          (key) =>
            key === "action" ||
            key === "action_reference_number" ||
            key === "scheduled_arrival_time",
        )
      ) {
        diff.hasActionOnlyChanges = true
      }
    }
  })

  // Calculate flags for passthrough vs non-passthrough changes
  diff.hasPassthroughChanges =
    diff.added.some((item) => item.stop.action === "passthrough") ||
    diff.removed.some((item) => item.stop.action === "passthrough") ||
    diff.modified.some((item) => item.stop.action === "passthrough")

  diff.hasNonPassthroughChanges =
    diff.added.some((item) => item.stop.action !== "passthrough") ||
    diff.removed.some((item) => item.stop.action !== "passthrough") ||
    diff.modified.some((item) => item.stop.action !== "passthrough")

  // Check for order changes
  // Only check if no stops were added, removed, or modified (location-wise)
  if (
    diff.added.length === 0 &&
    diff.removed.length === 0 &&
    diff.modified.length === 0
  ) {
    // Compare the order of stops with IDs
    const originalIds = originalStops
      .filter((stop) => stop.id)
      .map((stop) => stop.id)
    const updatedIds = updatedStops
      .filter((stop) => stop.id)
      .map((stop) => stop.id)

    // If same stops but different order
    if (
      originalIds.length === updatedIds.length &&
      originalIds.every((id) => updatedIds.includes(id)) &&
      originalIds.some((id, index) => id !== updatedIds[index])
    ) {
      diff.hasOrderChanges = true
    }
  }

  return diff
}

const ConnectingLine = ({
  solid,
  avoid,
}: {
  solid: boolean
  avoid: AvoidType[] | undefined
}) => {
  const { t } = useTranslation()
  return (
    <tr className="[&_td]:p-px">
      <td></td>
      <td className="relative">
        <div className="absolute inset-0 flex items-center justify-center">
          <div
            className={`h-[15px] w-px ${
              solid ? "border-solid" : "border-dotted"
            } border-l-2 border-gray-600 dark:border-gray-400`}
          />
        </div>
      </td>
      <td>
        {avoid !== undefined && (
          <span className="px-3 py-0 text-xs text-muted-foreground">
            {avoid && avoid.includes("tollRoads")
              ? t("maps.routeStopList.sectionAvoidsTolls")
              : t("maps.routeStopList.sectionAllowsTolls")}
          </span>
        )}
      </td>
      <td></td>
      <td></td>
    </tr>
  )
}

type CurrentLocationProps = {
  location: Location
  isReadOnlyMap?: boolean
}
const CurrentLocation = ({
  location,
  isReadOnlyMap = false,
}: CurrentLocationProps) => {
  const { t } = useTranslation()
  const [address, setAddress] = useState<Address | null>(null)
  const reverseGeocode = useReverseGeocoder()

  useEffect(() => {
    const fetchAddress = async () => {
      if (!location.lat || !location.lon) return
      try {
        const result = await reverseGeocode(location.lat, location.lon)
        setAddress(result)
      } catch (err) {
        captureError("Error", {
          origin: "UserAction",
          extra: {
            message:
              "Reverse geocoding of driver's current location with google maps",
          },
        })
      }
    }

    void fetchAddress()
  }, [location, reverseGeocode])

  const shortAddress = useMemo(
    () => (address ? `${address.countryCode}, ${address.city}` : null),
    [address],
  )

  const isSmallScreen = useMediaQuery("(max-width: 900px)")

  if (!address || !shortAddress) return null

  return (
    <tr data-testid="current-location-row">
      {!isMobile && !isSmallScreen && !isReadOnlyMap && (
        <td className="w-[23px]"></td>
      )}
      <td className="w-px">
        <div className="mb-[1px] flex min-h-[30px] min-w-[30px] items-center px-[10px] py-[5px]">
          <Locate size={16} />
        </div>
      </td>
      <td>
        <Popover
          disabled={isMobile}
          interactionKind="hover"
          hoverCloseDelay={0}
          position={Position.BOTTOM}
          content={
            <div className="w-full max-w-md bg-white p-4 text-sm dark:bg-gray-800">
              {t("maps.routeStopList.currentLocation", {
                address: address.formattedAddress,
              })}
            </div>
          }
        >
          <div className="px-[10px]">{shortAddress}</div>
        </Popover>
      </td>
      <td></td>
      <td></td>
      {!isMobile && !isSmallScreen && !isReadOnlyMap && <td></td>}
      {!isMobile && !isSmallScreen && !isReadOnlyMap && <td></td>}
    </tr>
  )
}

type NewStopViewProps = {
  onAdd: (stop: RouteStop) => void
  isUpdating: boolean
  onEditStart?: () => void
  onEditEnd?: () => void
  inTable?: boolean
}

const NewStopView = ({
  onAdd,
  isUpdating,
  onEditStart,
  onEditEnd,
  inTable = true,
}: NewStopViewProps) => {
  const { t } = useTranslation()
  const [isAdding, setIsAdding] = useState(false)

  const addStop = useCallback(
    (location: Location) => {
      setIsAdding(false)
      onEditEnd?.()
      onAdd(newStop(location))
    },
    [onAdd, onEditEnd],
  )

  const abortAddStop = () => {
    setIsAdding(false)
    onEditEnd?.()
  }

  const handleStartAdding = () => {
    onEditStart?.() // Close any existing edit mode
    setIsAdding(true)
  }
  if (!inTable) {
    return isAdding ? (
      <div className="">
        <LocationEditView onUpdate={addStop} onAbort={abortAddStop} />
      </div>
    ) : (
      <NewButton
        className="h-[31px] w-full bg-blue-600 px-6 text-white hover:bg-blue-700"
        data-testid="add-stop-button"
        onClick={handleStartAdding}
        disabled={isUpdating}
      >
        {t("maps.routeStopList.addNewStop")}
      </NewButton>
    )
  }
  return (
    <tr className="group">
      <td></td>
      <td>
        <Button
          minimal={true}
          disabled={isAdding || isUpdating}
          onClick={handleStartAdding}
          className="group-hover:bg-[#E9EBEE] dark:group-hover:bg-gray-700"
        >
          <Plus size={16} className="dark:text-gray-300" />
        </Button>
      </td>
      <td>
        {isAdding ? (
          <div className="">
            <LocationEditView onUpdate={addStop} onAbort={abortAddStop} />
          </div>
        ) : (
          <Button
            data-testid="add-stop-button"
            minimal={true}
            onClick={handleStartAdding}
            disabled={isUpdating}
            className="w-full justify-start bg-transparent group-hover:bg-[#E9EBEE] dark:group-hover:bg-gray-700"
          >
            <span className="text-s w-full font-bold text-gray-500 dark:text-gray-400">
              {t("maps.routeStopList.addNewStop")}
            </span>
          </Button>
        )}
      </td>
      <td></td>
      <td></td>
      <td></td>
      <td></td>
    </tr>
  )
}

type UpdateIndicatorProps = {
  isUpdateInProgress: boolean
}

const UpdateIndicator = ({ isUpdateInProgress }: UpdateIndicatorProps) => {
  if (!isUpdateInProgress) return null

  return (
    <div className={css.updateSpinner}>
      <Spinner size={25} />
    </div>
  )
}

type RouteStopListProps = {
  isReadOnlyMap?: boolean
  routeFromAnalyzeOrder?: Route2g | undefined
  isAnalyzeOrderMap?: boolean
  driverJid: string
  locationJid?: string
  onTemporaryRouteUpdate?: (route: Route2g, diff: RouteStopDiff) => void
  temporaryAvoidOptions?: AvoidType[]
  hideAddButton?: boolean
  onEditModeChange?: (isEditing: boolean) => void
}

const RouteStopList = ({
  isReadOnlyMap,
  routeFromAnalyzeOrder,
  onTemporaryRouteUpdate,
  isAnalyzeOrderMap,
  driverJid,
  locationJid,
  temporaryAvoidOptions,
  hideAddButton,
  onEditModeChange,
}: RouteStopListProps) => {
  const currentLocation = useAppSelector(
    selectLocation(locationJid ?? driverJid),
  )
  const driverRoute = useAppSelector(findRoute2g(driverJid))
  const route = isAnalyzeOrderMap ? routeFromAnalyzeOrder : driverRoute
  const routeStops = useMemo(() => route?.stops || [], [route?.stops])

  // Stop IDs from the driver's current route - these should be disabled in analyze order map
  const driverCurrentStopIds = useMemo(() => {
    if (!isAnalyzeOrderMap || !driverRoute?.stops) return new Set<string>()
    return new Set(
      driverRoute.stops
        .map((stop) => stop.id)
        .filter((id): id is string => id !== undefined),
    )
  }, [isAnalyzeOrderMap, driverRoute])
  const showLegDistance = useStopToStopDistance()
  const [stops, setStops] = useState<RouteStop[]>([])
  const [moveCompleted, setMoveCompleted] = useState(false)

  const [isUpdating, setIsUpdating] = useState(false)

  const [highlightedStopId, setHighlightedStopId] = useState<string | null>(
    null,
  )
  const [editingStopId, setEditingStopId] = useState<string | null>(null)

  const { upsertRoute2g, routeJid, highlightDuplicateStop } =
    useContext(DriverContext)

  const originalStopsRef = useRef<RouteStop[] | []>([])

  const processRouteStops = useCallback(() => {
    if (!routeJid && !isAnalyzeOrderMap) return

    if (!routeStops) {
      setStops([])
      return
    }
    if (isAnalyzeOrderMap && driverJid === "on-page") {
      setStops(routeStops)
    } else {
      setStops(
        routeStops.map((stop) => ({
          ...stop,
          is_active: isStopActive(stop),
          is_current_target: findCurrentTarget(routeStops) === stop,
          is_late: isStopLate(stop),
          isPending: isStopPending(stop),
          shouldAlert: shouldAlert(stop),
        })),
      )
    }
  }, [routeJid, driverJid, isAnalyzeOrderMap, routeStops])

  useEffect(() => {
    processRouteStops()
  }, [processRouteStops])

  useEffect(() => {
    if (!isAnalyzeOrderMap) {
      if (!highlightDuplicateStop.current) {
        highlightDuplicateStop.current = (stopId: string) => {
          setHighlightedStopId(stopId)
          setTimeout(() => setHighlightedStopId(null), 2000)

          // Locate stop and scroll
          const stopElement = document.getElementById(`route-stop-${stopId}`)
          if (stopElement) {
            stopElement.scrollIntoView({ behavior: "smooth", block: "center" })
          }
        }
      }
    }

    return () => {
      // Clear the ref when the component unmounts or driver changes
      if (!isAnalyzeOrderMap) {
        highlightDuplicateStop.current = null
      }
    }
  }, [highlightDuplicateStop, routeJid, isAnalyzeOrderMap])

  const updateRoute = useCallback(
    async (updatedStops: RouteStop[], originalStops: RouteStop[]) => {
      if ((!routeJid && !isAnalyzeOrderMap) || isUpdating) return

      try {
        setIsUpdating(true)
        if (isAnalyzeOrderMap && onTemporaryRouteUpdate) {
          const diff = calculateRouteStopDiff(originalStops, updatedStops)
          onTemporaryRouteUpdate(
            {
              stops: updatedStops,
              jid: routeJid || "",
            },
            diff,
          )
        } else if (routeJid) {
          await upsertRoute2g({ stops: updatedStops, jid: routeJid }, routeJid)
        }
      } catch (error) {
        setStops(originalStops)
      } finally {
        originalStopsRef.current = []
        setIsUpdating(false)
      }
    },
    [
      routeJid,
      isAnalyzeOrderMap,
      isUpdating,
      onTemporaryRouteUpdate,
      upsertRoute2g,
    ],
  )

  useEffect(() => {
    if (!routeJid && !isAnalyzeOrderMap) return
    if (!moveCompleted) return
    if (isUpdating) return

    setMoveCompleted(false)
    // If originalStopsRef is empty, it means the drag was cancelled
    // so we should restore the original order instead of persisting changes
    if (originalStopsRef.current.length === 0) {
      // This shouldn't happen, but just in case
      updateRoute(stops, [])
    } else {
      updateRoute(stops, originalStopsRef.current)
    }
  }, [
    moveCompleted,
    setMoveCompleted,
    stops,
    routeJid,
    updateRoute,
    isUpdating,
    isAnalyzeOrderMap,
  ])

  const updateStop = useCallback(
    async (stop: RouteStop, type: string) => {
      if ((!routeJid && !isAnalyzeOrderMap) || isUpdating) return
      const updated = stops.map((s) => (s.id === stop.id ? stop : s))
      const originalStops = [...stops]
      setStops(updated)
      updateRoute(updated, originalStops)
      if (!stop.is_temporary) {
        window.analytics.track("UpdateRouteStop", {
          id: stop.id,
          addedBy: stop.by,
          updateType: type,
        })
      } else {
        window.analytics.track(
          window.anonymousXMPP
            ? "Anonymous_UpdateRouteStop"
            : "OrderOnPage_UpdateRouteStop",
          {
            originalStops,
            updated,
          },
        )
      }
    },
    [routeJid, isAnalyzeOrderMap, isUpdating, stops, updateRoute],
  )

  const addStop = useCallback(
    async (stop: RouteStop) => {
      if ((!routeJid && !isAnalyzeOrderMap) || isUpdating) return
      const updated = [...stops, stop]
      const index = stops.filter((stop) => isStopPending(stop)).length
      const originalStops = [...stops]
      setStops(updated)
      updateRoute(updated, originalStops)
      if (!stop.is_temporary) {
        window.analytics.track("AddRouteStop", {
          source: "list",
          index: index,
          multi: index > 0,
        })
      } else {
        window.analytics.track(
          window.anonymousXMPP
            ? "Anonymous_AddRouteStop"
            : "OrderOnPage_AddRouteStop",
          {
            stop,
          },
        )
      }
    },
    [routeJid, isAnalyzeOrderMap, isUpdating, stops, updateRoute],
  )

  const removeStop = useCallback(
    async (stop: RouteStop) => {
      if ((!routeJid && !isAnalyzeOrderMap) || isUpdating) return
      const updated = stops.filter((s) => s.id !== stop.id)
      const originalStops = [...stops]
      setStops(updated)
      updateRoute(updated, originalStops)
      if (!isAnalyzeOrderMap && !stop.is_temporary) {
        window.analytics.track("RemoveRouteStop", {
          id: stop.id,
          addedBy: stop.by,
        })
      } else {
        window.analytics.track(
          window.anonymousXMPP
            ? "Anonymous_RemoveRouteStop"
            : "OrderOnPage_RemoveRouteStop",
          {
            stop,
          },
        )
      }
    },
    [routeJid, isAnalyzeOrderMap, isUpdating, stops, updateRoute],
  )

  const moveStop = useCallback(
    async (from: number, to: number) => {
      if (isUpdating) return
      if (originalStopsRef.current.length === 0) {
        originalStopsRef.current = [...stops]
      }
      setStops(moveRouteStop(stops, from, to))
    },
    [stops, isUpdating],
  )

  const cancelDrag = useCallback(() => {
    // Restore original order when drag is cancelled
    if (originalStopsRef.current.length > 0) {
      setStops(originalStopsRef.current)
      originalStopsRef.current = []
    }
  }, [])

  const rawShouldDisplayCurrentLocation =
    (!routeStops ||
      routeStops.length === 0 ||
      (routeStops[0].proximity_status &&
        ["arriving", "far"].includes(routeStops[0].proximity_status))) &&
    !isAnalyzeOrderMap &&
    !routeJid

  // Debounce the value (300ms is typical, adjust as needed)
  const shouldDisplayCurrentLocation =
    useDebouncedValue(rawShouldDisplayCurrentLocation, 3000) ||
    (isAnalyzeOrderMap && routeJid)

  const handleEditStart = useCallback(
    (stopId: string) => {
      setEditingStopId(stopId)
      onEditModeChange?.(true)
    },
    [onEditModeChange],
  )

  const handleEditEnd = useCallback(() => {
    setEditingStopId(null)
    onEditModeChange?.(false)
  }, [onEditModeChange])

  const showStopNumbers =
    useFeatureFlagEnabled(STOP_NUMBERS_FLAG) === true && !isAnalyzeOrderMap
  const numbers = useMemo(
    () => (showStopNumbers ? stopNumbers(stops) : []),
    [showStopNumbers, stops],
  )

  const Stop = ({ stop, index }: { stop: RouteStop; index: number }) => {
    const carRouteConfig = useAppSelector(
      findRouteConfig(driverJid || undefined),
    )
    const hasGlobalAvoidTolls = temporaryAvoidOptions
      ? temporaryAvoidOptions.includes("tollRoads")
      : Boolean(
          carRouteConfig?.avoid && carRouteConfig?.avoid.includes("tollRoads"),
        )

    const nextStop = index < stops.length - 1 ? stops[index + 1] : null
    const isNextStopCurrentTarget = Boolean(nextStop?.is_current_target)

    const isHighlighted = stop.id === highlightedStopId
    const isSmallScreen = useMediaQuery("(max-width: 900px)")
    const isDriverCurrentStop =
      stop.id !== undefined && driverCurrentStopIds.has(stop.id)

    let avoid = nextStop?.avoid

    if (hasGlobalAvoidTolls ? avoid?.includes("tollRoads") : !avoid?.length) {
      avoid = undefined
    }

    return (
      <>
        <RouteStopView
          isReadOnlyMap={isReadOnlyMap}
          isDisabled={isDriverCurrentStop}
          index={index}
          stopNumber={numbers[index]}
          stop={stop}
          displayedDistance={getDisplayedStopDistance(
            stops,
            index,
            showLegDistance,
          )}
          onUpdate={updateStop}
          onRemove={removeStop}
          onMove={moveStop}
          onMoveCompleted={() => {
            setMoveCompleted(true)
          }}
          onDragCancelled={cancelDrag}
          isHighlighted={isHighlighted}
          isUpdating={isUpdating}
          isEditing={editingStopId === stop.id}
          onEditStart={() => stop.id && handleEditStart(stop.id)}
          onEditEnd={handleEditEnd}
          driverJid={driverJid !== "on-page" ? driverJid : undefined}
          stationarySince={
            stop.is_current_target
              ? currentLocation?.stationarySince
              : undefined
          }
          temporaryAvoidOptions={temporaryAvoidOptions}
          hideAvoid={isAnalyzeOrderMap}
        />
        {nextStop && !isMobile && !isSmallScreen && !isReadOnlyMap && (
          <ConnectingLine solid={isNextStopCurrentTarget} avoid={avoid} />
        )}
      </>
    )
  }
  const isSmallScreen = useMediaQuery("(max-width: 900px)")

  // Callback for when NewStopView starts adding
  const handleNewStopEditStart = useCallback(() => {
    handleEditEnd() // Close any existing edit mode
    onEditModeChange?.(true)
  }, [handleEditEnd, onEditModeChange])

  if (isAnalyzeOrderMap && !routeJid && stops.length === 0) {
    return (
      <div className={isUpdating ? css.updateInProgress : "w-full"}>
        {isUpdating && <UpdateIndicator isUpdateInProgress={isUpdating} />}
        {!isMobile && !isSmallScreen && !isReadOnlyMap && !hideAddButton && (
          <NewStopView
            onAdd={addStop}
            isUpdating={isUpdating}
            onEditStart={handleNewStopEditStart}
            onEditEnd={handleEditEnd}
            inTable={false}
          />
        )}
      </div>
    )
  }

  return (
    <div className={isUpdating ? css.updateInProgress : "w-full"}>
      {isUpdating && <UpdateIndicator isUpdateInProgress={isUpdating} />}

      <table
        className={`w-full [&_td]:p-[3px] ${isMobile || isSmallScreen ? "text-sm" : ""}`}
      >
        <tbody className="dark:text-gray-200">
          {shouldDisplayCurrentLocation && (
            <>
              <CurrentLocation
                location={currentLocation!}
                isReadOnlyMap={isReadOnlyMap}
              />
              {stops.length > 0 &&
                !isMobile &&
                !isSmallScreen &&
                !isReadOnlyMap && (
                  <ConnectingLine solid={true} avoid={stops[0].avoid} />
                )}
            </>
          )}
          {stops.map((stop, index) => (
            <Stop stop={stop} index={index} key={index} />
          ))}
        </tbody>
        <tfoot
          id="new-stop-button"
          className="sticky inset-0 w-full bg-[var(--panel-color)]"
        >
          {!isMobile && !isSmallScreen && !isReadOnlyMap && !hideAddButton && (
            <NewStopView
              onAdd={addStop}
              isUpdating={isUpdating}
              onEditStart={handleNewStopEditStart}
              onEditEnd={handleEditEnd}
            />
          )}
        </tfoot>
      </table>
    </div>
  )
}

export default memo(RouteStopList)
